import React, { useState, useRef, useCallback, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import ChitraHeader from "@/components/chitra/ChitraHeader";
import { BackgroundWaves } from "@/components/BackgroundVideo";
import { useSupabase } from "@/hooks/useSupabase";
import { RateLimitModal } from "@/components/RateLimitModal";
import { AuthModal } from "@/components/AuthModal";
import { ChitraIntroModal } from "@/components/ChitraIntroModal";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { cn } from "@/lib/utils";
import dragDropImage from "@/assets/oreog-drag-and-drop.png";
import FullscreenImageModal from "@/components/chitra/FullscreenImageModal";
import VideoFullscreenModal from "@/components/chitra/VideoFullscreenModal";
import { UpgradeModal } from "@/components/UpgradeModal";

export default function ChitraLayout() {
    const { user } = useSupabase();
    const isDarkMode = useIsDarkMode();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [showRateLimitModal, setShowRateLimitModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeResetDate, setUpgradeResetDate] = useState<string | undefined>(undefined);
    const [isDragging, setIsDragging] = useState(false);
    const [fullscreenImage, setFullscreenImage] = useState<any | null>(null);
    const [fullscreenVideo, setFullscreenVideo] = useState<any | null>(null);
    const [fullscreenImageLoading, setFullscreenImageLoading] = useState(false);
    const fullscreenOnUseImageRef = useRef<((dataUrl: string) => void) | null>(null);
    const lastScrollY = useRef(0);
    const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);

    useEffect(() => {
        const onHotkey = (e: KeyboardEvent) => {
            if (e.repeat) return;
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName?.toLowerCase();
            const isTyping =
                tag === 'input' ||
                tag === 'textarea' ||
                (target as any)?.isContentEditable;

            if (isTyping) return;

            const isCtrlB = e.ctrlKey && (e.key === 'b' || e.key === 'B');
            if (isCtrlB) {
                e.preventDefault();
                setIsOpen((prev) => !prev);
                return;
            }

            const isCtrlK = e.ctrlKey && (e.key === 'k' || e.key === 'K');
            if (isCtrlK) {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('chitra:openSearch'));
            }
        };

        window.addEventListener('keydown', onHotkey);
        const onSearchOverlayEvent = (e: Event) => {
            try {
                const anyEvent = e as any;
                const open = !!anyEvent.detail?.open;
                setIsSearchOverlayOpen(open);
            } catch { }
        };

        window.addEventListener('chitra:searchOverlayState', onSearchOverlayEvent as any);

        return () => {
            window.removeEventListener('keydown', onHotkey);
            window.removeEventListener('chitra:searchOverlayState', onSearchOverlayEvent as any);
        };
    }, []);

    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
            setIsHeaderVisible(false);
        } else {
            setIsHeaderVisible(true);
        }
        lastScrollY.current = currentScrollY;
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    // Handle drag and drop events for the entire window
    useEffect(() => {
        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDragging) setIsDragging(true);
        };

        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // Only hide if we're leaving the window
            if (isDragging && e.relatedTarget === null) {
                setIsDragging(false);
            }
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
        };
        window.addEventListener("dragover", handleDragOver);
        window.addEventListener("dragleave", handleDragLeave);
        window.addEventListener("drop", handleDrop);
        return () => {
            window.removeEventListener("dragover", handleDragOver);
            window.removeEventListener("dragleave", handleDragLeave);
            window.removeEventListener("drop", handleDrop);
        };
    }, [isDragging]);

    const downloadImage = async (imageUrl: string) => {
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `chitra-ai-${Date.now()}.webp`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openImageFullscreen = useCallback((img: any, onUseImage?: (dataUrl: string) => void) => {
        setFullscreenImage(img);
        fullscreenOnUseImageRef.current = onUseImage || null;
        setFullscreenImageLoading(true);
        const i = new Image();
        i.src = img.url;
        i.onload = () => setFullscreenImageLoading(false);
        i.onerror = () => setFullscreenImageLoading(false);
    }, []);

    const openVideoFullscreen = useCallback((video: any) => {
        setFullscreenVideo(video);
    }, []);

    return (
        <div
            className={cn(
                "min-h-screen w-full relative overflow-x-hidden",
                isDarkMode ? "dark" : ""
            )}
        >
            <ChitraIntroModal isDarkMode={isDarkMode} />

            <div className="relative flex w-full bg-background text-foreground">
                <BackgroundWaves isDarkMode={isDarkMode} className="z-0" />
                <Sidebar
                    isOpen={isOpen}
                    onToggle={() => setIsOpen(!isOpen)}
                    onNewChat={() => navigate("/")}
                    isDarkMode={isDarkMode}
                    onAuth={() => setShowAuthModal(true)}
                    user={user}
                />
                <div className="flex flex-col flex-1 relative" style={{ zIndex: 1 }}>
                    <ChitraHeader
                        isOpen={isOpen}
                        onToggle={() => setIsOpen(!isOpen)}
                        isDarkMode={isDarkMode}
                        isHeaderVisible={isHeaderVisible && !isSearchOverlayOpen}
                    />

                    <main
                        className="flex-1 p-2 sm:p-4 pt-20 sm:pt-20 md:p-6 md:pt-24 lg:p-8 lg:pt-28 relative z-0 w-[100vw]"
                        style={{ isolation: "isolate" }}
                    >
                        <Outlet context={{
                            isDarkMode,
                            setShowAuthModal,
                            setShowRateLimitModal,
                            setShowUpgradeModal,
                            setUpgradeResetDate,
                            user,
                            openImageFullscreen,
                            openVideoFullscreen,
                        }} />
                    </main>
                </div>
            </div>

            <RateLimitModal
                isOpen={showRateLimitModal}
                onClose={() => setShowRateLimitModal(false)}
                onAuthSuccess={() => {
                    setShowRateLimitModal(false);
                }}
                isDarkMode={isDarkMode}
            />

            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                suggestedPlan="pro"
                title="Upgrade Required"
                message="You have reached your daily image generation limit. Upgrade to continue."
                resetDate={upgradeResetDate}
            />

            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onAuth={(user) => {
                    setShowAuthModal(false);
                }}
                isDarkMode={isDarkMode}
            />

            {fullscreenImage && (
                <FullscreenImageModal
                    image={fullscreenImage as any}
                    isLoading={fullscreenImageLoading}
                    onClose={() => setFullscreenImage(null)}
                    onDownload={downloadImage}
                    isDarkMode={isDarkMode}
                    onUseImage={(dataUrl: string) => {
                        if (fullscreenOnUseImageRef.current) {
                            fullscreenOnUseImageRef.current(dataUrl);
                        }
                        setFullscreenImage(null);
                    }}
                />
            )}

            {fullscreenVideo && (
                <VideoFullscreenModal
                    isOpen={true}
                    src={fullscreenVideo.video_url || ''}
                    poster={fullscreenVideo.thumbnail_url}
                    isDarkMode={isDarkMode}
                    onClose={() => setFullscreenVideo(null)}
                    creatorName={fullscreenVideo.creator_name}
                />
            )}
        </div>
    );
}
